import * as THREE from "three";
import { walkingWith } from "../../sim/backpack";
import { BAIL_TIME, type Skater } from "../../skater";
import type { FlipAnim, GrindStyle } from "../../tricks";
import { setArm } from "../arm";
import { MAX_HIP, MIN_HIP } from "../ik";
import { clamp } from "../pose";
import type { SkaterLook, SkaterRig } from "../skater-parts";
import { easeBy, footTo, gripWith, Held, heldGrab, RidePose, type RideCue, type RideRig } from "./ride-pose";
import {
  buildScooter,
  SCOOTER_DECK_BACK,
  SCOOTER_DECK_FRONT,
  SCOOTER_DECK_THICK,
  SCOOTER_DECK_Y,
  SCOOTER_GRIP,
  SCOOTER_HEAD_Z,
  SCOOTER_REAR_HUB,
  SCOOTER_WHEEL_R,
  type Scooter,
} from "./scooter";

// The rider on the scooter (#139), posed over the board's pose each frame by
// `SkaterModel`, after the bike's. The meshes are in scooter.ts; getting on
// and off, the arm rate limit, the hands on the grips and walking it beside
// you are the rides' shared `RidePose`. It reads the same eased values off
// the board pose as the bike does (`RideCue`).

export class ScooterPose implements RideRig {
  /** The scooter, built the first time someone gets on one; and how far its wheels have turned. */
  private scooter: Scooter | null = null;
  private wheelTurn = 0;
  private readonly ride: RidePose;
  /** The scooter's pitch, yaw and roll and its offset (manual, grind, stall), eased. */
  private pitch = 0;
  private yaw = 0;
  private roll = 0;
  private readonly off = new THREE.Vector3();
  private crouch = 0;
  /** The grab's amount and name, eased; and how far the hands are on the grips. */
  /** The grab the hands are in, eased; switching lets go first. */
  private grab = new Held<string>(18);
  private grip = 1;
  /** How far the legs are stretched out behind for a Superman (0..1, at `STRETCH_RATE`). */
  private stretch = 0;
  /** Where the back foot is in its kick (radians round), and how much it's kicking (0..1, eased). */
  private kickPhase = 0;
  private kick = 0;
  /** Walked beside the rider this frame (`walkBeside`), so `apply` leaves the scooter where that put it. */
  private walked = false;

  constructor(
    private rig: SkaterRig,
    private look: SkaterLook,
  ) {
    this.ride = new RidePose(rig);
  }

  /**
   * On the scooter (#139): the board's hidden and the rider stands on the
   * deck facing the bars, front foot ahead of the back one, hands on the
   * grips. Rolling along at less than a crouch, the back foot kicks to
   * push. It pitches up onto the back wheel for a manual, lays the deck on
   * the rail in a grind (`DECK_GRINDS`) and stands on one end on the
   * coping; flips whip the deck round the stem, spin the bars, flip the
   * deck under the bars or spin the whole scooter (`whipScooter`); grabs
   * take a hand or a foot off; a bail throws the rider over the bars.
   * Returns whether it posed the rider (so the model re-reads the arms' raise).
   */
  apply(sk: Skater, dt: number, cue: Readonly<RideCue>): boolean {
    const on = sk.ride === "scooter" && sk.mode !== "foot";
    const onW = this.ride.ease(on, dt);
    if (this.scooter) this.scooter.root.visible = on || this.walked;
    if (onW === 0) return false;
    const sc = this.ensureScooter();
    sc.root.visible = on || this.walked;
    if (on) this.rig.board.visible = false;
    this.ride.saveJoints();
    if (sk.mode === "ground") this.wheelTurn += (sk.speed * dt) / SCOOTER_WHEEL_R;
    for (const w of sc.wheels) w.rotation.x = this.wheelTurn;
    sc.deck.rotation.set(0, 0, 0);
    sc.front.rotation.set(0, 0, 0);

    // Grabs ease in and out; switching grabs lets go of the last one first.
    // A grab in a body flip lets go in time for the hands to be back on the bars.
    const grab = heldGrab(sk);
    this.grab.update(grab, dt);

    // Pitch about a point on the deck or a wheel: up on the back wheel for a
    // manual (the front for a nose manual), the deck laid on the rail its
    // grind's way, stood up on one end on the coping.
    let pitch = 0;
    let yaw = 0;
    let roll = 0;
    const pivot = SC_PIVOT.set(0, SCOOTER_WHEEL_R, cue.manualTip < 0 ? SCOOTER_HEAD_Z + SCOOTER_REAR_HUB : SCOOTER_HEAD_Z);
    const offTarget = TMP_V.set(0, 0.1 * cue.airTuck, 0);
    if (Math.abs(cue.manualTip) > 0.01) pitch = 0.4 * cue.manualTip + 0.06 * cue.manualWobble * Math.abs(cue.manualTip);
    const deckUnder = SCOOTER_DECK_Y - SCOOTER_DECK_THICK;
    if (sk.mode === "grind" && sk.grind) {
      const d = DECK_GRINDS[sk.grind.trick.style] ?? DECK_GRINDS.fiftyfifty!;
      pitch = d.pitch;
      yaw = d.yaw;
      roll = d.roll;
      // The deck's underside at its pivot rests on the rail (model y 0, x 0, z 0).
      const pz = d.pivot === "back" ? SCOOTER_DECK_BACK + 0.04 : d.pivot === "front" ? SCOOTER_DECK_FRONT - 0.04 : 0;
      pivot.set(0, deckUnder, pz);
      offTarget.set(0, -deckUnder, -pz);
    } else if (sk.mode === "lip" && sk.lip) {
      const style = sk.lip.trick.style;
      if (style === "nose") {
        pitch = 1.1;
        pivot.set(0, 0, SCOOTER_HEAD_Z);
        offTarget.set(0, 0, -SCOOTER_HEAD_Z);
      } else if (style === "disaster") {
        pitch = -0.3;
        pivot.set(0, deckUnder, 0);
        offTarget.set(0, -deckUnder, 0);
      } else {
        pitch = -1.1;
        pivot.set(0, 0, SCOOTER_HEAD_Z + SCOOTER_REAR_HUB);
        offTarget.set(0, 0, -(SCOOTER_HEAD_Z + SCOOTER_REAR_HUB));
      }
    }
    const k = easeBy(16, dt);
    this.pitch += (pitch - this.pitch) * k;
    this.yaw += (yaw - this.yaw) * k;
    this.roll += (roll - this.roll) * k;
    this.off.lerp(offTarget, k);
    const q = SC_Q.setFromAxisAngle(X_AXIS, this.pitch);
    if (this.yaw !== 0 || this.roll !== 0) q.premultiply(TMP_Q4.setFromAxisAngle(Z_AXIS, this.roll)).premultiply(TMP_Q3.setFromAxisAngle(Y_AXIS, this.yaw));
    const base = SC_BASE.copy(pivot).sub(TMP_V2.copy(pivot).applyQuaternion(q)).add(this.off);
    if (!this.walked) {
      sc.root.quaternion.copy(q);
      sc.root.position.copy(base);
      this.ride.mountFrom(sc.root);
    } else this.ride.dismountTo(sc.root);

    // The move in progress, from a flip or a manual's flatground pop.
    const pop = sk.manual?.pop;
    const move = sk.flip
      ? { anim: sk.flip.trick.anim, n: sk.flip.trick.turns, p: Math.min(1, sk.flip.t / sk.flip.duration) }
      : pop
        ? { anim: pop.trick.anim, n: pop.trick.rotations, p: Math.min(1, pop.t / pop.duration) }
        : null;
    // Whipping it, the feet come up off the deck: the knees tuck as it goes round under them.
    const tuck = move ? Math.sin(Math.PI * move.p) : 0;

    // The rider: on the deck facing the bars (+z), leaning into turns.
    const riderQ = TMP_Q3.setFromAxisAngle(Y_AXIS, Math.PI / 2);
    riderQ.premultiply(TMP_Q4.setFromAxisAngle(Z_AXIS, -0.6 * cue.lean));
    riderQ.premultiply(q);
    const at = TMP_V2.set(0, SCOOTER_DECK_Y + 0.02 + FEET_LIFT * tuck, STAND_Z);
    this.crouch += (sk.crouch - this.crouch) * easeBy(12, dt);
    const crouch = this.crouch;
    let hipY = clamp(STAND_HIP - 0.2 * crouch - 0.1 * cue.airTuck - FEET_LIFT * tuck + 0.6 * cue.landDip, MIN_HIP, MAX_HIP);
    const superman = this.part("Superman");
    if (superman > 0) {
      // Feet off the back, body stretched out flat behind the bars.
      riderQ.premultiply(TMP_Q4.setFromAxisAngle(X_AXIS, 0.85 * superman));
      at.z -= 0.3 * superman;
      at.y += 0.3 * superman;
    }
    // The legs straighten out behind at a steady rate, not eased: near
    // straight the knee turns a lot for a little hip (as on the bike, #138).
    const stretchStep = STRETCH_RATE * dt;
    this.stretch += clamp(superman - this.stretch, -stretchStep, stretchStep);
    hipY += (MAX_HIP - hipY) * this.stretch;
    if (sk.mode === "bail") {
      // Over the bars: the rider dives forward and down, the scooter flips back behind.
      const fall = Math.min(1, sk.bailT / 0.3);
      const getUp = Math.max(0, (sk.bailT - (BAIL_TIME - 0.35)) / 0.35);
      const p = fall * (1 - getUp);
      riderQ.premultiply(TMP_Q4.setFromAxisAngle(X_AXIS, 1.5 * p));
      at.z += 0.8 * p;
      at.y -= 0.05 * p;
      const t = sk.bailT;
      if (t < BAIL_TIME - 0.3) {
        sc.root.rotation.x -= Math.min(Math.PI, t * 6);
        sc.root.position.z -= Math.min(1, t * 2.5);
        sc.root.position.y += Math.sin(Math.min(t * 4, Math.PI)) * 0.3;
      }
    }
    this.rig.rider.quaternion.copy(riderQ);
    this.rig.rider.position.copy(at.applyQuaternion(q)).add(base);
    this.rig.hips.position.set(0, hipY, 0);
    this.rig.torso.rotation.set(0, 0, 0.38 + 0.3 * crouch + 0.2 * tuck, "YZX");
    this.rig.head.rotation.set(0, 0, -0.15 - 0.2 * crouch, "ZYX");

    // Kick to push (#139): rolling along on the ground, not crouched, the
    // back foot steps off beside the deck to the ground and sweeps back, the
    // standing knee bending to reach, a kick a stride.
    const kicking = sk.mode === "ground" && !sk.manual && crouch < 0.3 && sk.speed > 0.5;
    this.kick += ((kicking ? 1 : 0) - this.kick) * easeBy(6, dt);
    if (this.kick > 0.001) this.kickPhase = (this.kickPhase + KICK_RATE * dt) % (Math.PI * 2);
    else this.kickPhase = 0;
    const down = Math.max(0, Math.sin(this.kickPhase)) * this.kick;
    const sweep = ((1 - Math.cos(this.kickPhase)) / 2) * this.kick;
    hipY += (Math.min(hipY, KICK_HIP) - hipY) * down;
    this.rig.hips.position.y = hipY;
    // The front foot (legs[0], +x) ahead on the deck, the back one behind it; both in over the deck's middle.
    for (let i = 0; i < this.rig.legs.length; i++) {
      const leg = this.rig.legs[i]!;
      const back = i === 1;
      const ahead = back ? BACK_FOOT_Z - KICK_SWEEP * sweep : FRONT_FOOT_Z;
      const drop = back ? (SCOOTER_DECK_Y + 0.02) * down : 0;
      footTo(leg, hipY + drop, ahead - STAND_Z, -leg.z * (back ? 0.75 - 1.2 * down : 0.75));
    }
    const noFooter = this.part("No-Footer");
    this.rig.legs[1]!.hip.rotation.x -= 0.9 * Math.max(this.part("One-Footer"), noFooter);
    this.rig.legs[0]!.hip.rotation.x += 0.9 * noFooter - 1.1 * this.part("Can-Can");
    // Supermanned, the legs trail out behind off the deck.
    if (superman > 0) for (const leg of this.rig.legs) leg.hip.rotation.z += 0.5 * superman;

    // Hands on the grips, eased off them for a No-Hander (arms out wide),
    // while the bars or the whole scooter spin round, or for a bail.
    const handsOff = move !== null && HANDS_OFF.has(move.anim);
    const gripping = sk.mode !== "bail" && !handsOff && !(grab !== null && grab === this.grab.name && hasPart(grab, "No-Hander"));
    this.grip += ((gripping ? 1 : 0) - this.grip) * easeBy(10, dt);
    if (sk.mode !== "bail") {
      setArm(this.rig.armFront, -1, ARMS_OUT);
      setArm(this.rig.armBack, 1, ARMS_OUT);
    }
    // A bri flip turns the scooter about the grips, before the hands reach for them.
    if (move) whipScooter(sc, move.anim, move.n, move.p);
    if (this.grip > 0.001) {
      this.rig.root.updateMatrixWorld(true);
      for (let a = 0; a < 2; a++) {
        const arm = a === 0 ? this.rig.armFront : this.rig.armBack;
        const side = a === 0 ? 1 : -1;
        gripWith(arm, sc.front.localToWorld(TMP_V.set(side * SCOOTER_GRIP.x, SCOOTER_GRIP.y, SCOOTER_GRIP.z)), this.grip, true);
      }
    }
    this.ride.limitArms(sk.mode !== "bail" && onW === 1, dt);
    this.ride.blendJoints();
    return true;
  }

  /**
   * On foot with the scooter out of the pack: walked beside the rider on the
   * front arm's side, upright, the front hand on the near grip and the
   * wheels rolling with the walk. `out` (0..1, eased by the model) blends the
   * arm to the grip. Run before `apply`, as the bike's is.
   * Returns whether it posed the arm.
   */
  walkBeside(sk: Skater, dt: number, out: number): boolean {
    this.walked = false;
    if (out <= 0) return false;
    const sc = this.ensureScooter();
    const side = Math.sign(this.rig.armFront.shoulder.position.z) || 1;
    if (walkingWith(sk) === "scooter") {
      this.walked = true;
      sc.root.visible = true;
      sc.root.position.set(side * (WALK_HAND_OUT + SCOOTER_GRIP.x), 0, WALK_Z);
      sc.root.rotation.set(0, 0, 0);
      this.ride.noteBeside(sc.root);
      sc.deck.rotation.set(0, 0, 0);
      sc.front.rotation.set(0, 0, 0);
      this.wheelTurn += ((sk.foot?.speed ?? 0) * dt) / SCOOTER_WHEEL_R;
      for (const w of sc.wheels) w.rotation.x = this.wheelTurn;
    }
    if (!this.walked) {
      // Getting on something else, the hand just lets go (the other ride's pose takes the arm).
      if (sk.mode !== "foot" && sk.ride !== "scooter") return false;
      this.ride.holdBeside(null, sk.mode === "foot" ? out : 1);
      return true;
    }
    this.rig.root.updateMatrixWorld(true);
    this.ride.holdBeside(sc.front.localToWorld(TMP_V.set(-side * SCOOTER_GRIP.x, SCOOTER_GRIP.y, SCOOTER_GRIP.z)), out);
    return true;
  }

  private ensureScooter(): Scooter {
    if (!this.scooter) {
      this.scooter = buildScooter(this.look);
      this.rig.yawGroup.add(this.scooter.root);
    }
    return this.scooter;
  }

  /** How much of the grab `part` the rider is showing (0..1, eased). */
  private part(part: ScooterPart): number {
    return this.grab.name !== null && hasPart(this.grab.name, part) ? this.grab.value : 0;
  }
}

/**
 * The scooter's moves for the flip ids its tricks use (`sim/rides.ts`), `p`
 * 0..1 of the way: kickflip a tailwhip (the deck round the stem), varial a
 * heel whip (the other way), heelflip a barspin, impossible a bri flip (the
 * whole scooter flipped forward about the grips, so the hands stay on),
 * treflip a finger whip (the whole scooter round the stem), and the
 * whip-and-barspin pairs.
 */
export function whipScooter(sc: Scooter, anim: FlipAnim, n: number, p: number): void {
  const turn = Math.PI * 2 * n * p;
  switch (anim) {
    case "kickflip":
      sc.deck.rotation.y += turn;
      break;
    case "varial":
      sc.deck.rotation.y -= turn;
      break;
    case "heelflip":
      sc.front.rotation.y += turn;
      break;
    case "impossible":
      turnRootAbout(sc.root, X_AXIS, turn, GRIP_LINE);
      break;
    case "treflip":
      turnRootAbout(sc.root, Y_AXIS, turn, STEM_AXIS);
      break;
    case "hardflip":
      sc.deck.rotation.y += turn;
      sc.front.rotation.y += turn;
      break;
    case "varialheel":
      sc.deck.rotation.y -= turn;
      sc.front.rotation.y += turn;
      break;
    default:
      sc.deck.rotation.y += turn;
  }
}

/** Turns `root` by `angle` about `axis` through `at` (both in its own space). */
function turnRootAbout(root: THREE.Object3D, axis: THREE.Vector3, angle: number, at: THREE.Vector3): void {
  const r = TMP_Q5.setFromAxisAngle(axis, angle);
  // p -> at + r(p - at): the position moves by Q(at - r at).
  const shift = TMP_V3.copy(at).sub(TMP_V4.copy(at).applyQuaternion(r)).applyQuaternion(root.quaternion);
  root.position.add(shift);
  root.quaternion.multiply(r);
}

/**
 * How the deck sits on the rail in each grind (#139), keyed by the grind
 * style the ride's table gives it (`sim/rides.ts`). The rail runs along the
 * scooter (model z) under its middle. `pivot` is the deck's point on the
 * rail (its back end, front end or middle); `pitch` turns it about there
 * (positive drops the front), `yaw` turns it across the rail, `roll` tips it.
 */
interface DeckGrind {
  pivot: "back" | "front" | "middle";
  pitch: number;
  yaw: number;
  roll: number;
}

const DECK_GRINDS: Readonly<Partial<Record<GrindStyle, DeckGrind>>> = {
  // The deck flat along the rail.
  fiftyfifty: { pivot: "middle", pitch: 0, yaw: 0, roll: 0 },
  // On the front end, the back wheel up behind.
  nosegrind: { pivot: "front", pitch: 0.3, yaw: 0, roll: 0 },
  // On the back end, the front wheel up.
  fiveo: { pivot: "back", pitch: -0.3, yaw: 0, roll: 0 },
  // Across the rail, the rail under the deck's middle.
  boardslide: { pivot: "middle", pitch: 0, yaw: Math.PI / 2, roll: 0 },
  // Across it the other way round.
  lipslide: { pivot: "middle", pitch: 0, yaw: -Math.PI / 2, roll: 0 },
  // On the front end, angled in toward you.
  crooked: { pivot: "front", pitch: 0.2, yaw: -0.35, roll: 0.15 },
  // On the front end, angled out over the far side.
  overcrook: { pivot: "front", pitch: 0.2, yaw: 0.4, roll: -0.15 },
  // On the back end, the front dropped down on your side.
  smith: { pivot: "back", pitch: 0.3, yaw: -0.3, roll: 0.25 },
  // On the back end, the front over the far side of the rail.
  feeble: { pivot: "back", pitch: 0.2, yaw: 0.35, roll: -0.2 },
};

/**
 * The hands and feet off the scooter `apply` knows how to pose; a grab is
 * posed by name as one of these. `scooter-pose.test.ts` checks every
 * scooter grab resolves to one, so renaming a trick can't drop its pose.
 */
export const SCOOTER_POSE_PARTS = ["No-Hander", "Superman", "No-Footer", "One-Footer", "Can-Can"] as const;
export type ScooterPart = (typeof SCOOTER_POSE_PARTS)[number];

function hasPart(name: string, part: ScooterPart): boolean {
  return name === part;
}

/** The parts a grab is posed as; empty if `apply` doesn't know it (it rides on in the plain pose). */
export function scooterPartsOf(name: string): ScooterPart[] {
  return SCOOTER_POSE_PARTS.filter((part) => hasPart(name, part));
}

/** Whether a grind style has its own way of laying the deck on the rail (otherwise it's a 50-50). */
export function hasDeckGrind(style: GrindStyle): boolean {
  return Object.hasOwn(DECK_GRINDS, style);
}

/** The moves that take the hands off the bars (`whipScooter`): the bars or the whole scooter spin round. */
const HANDS_OFF: ReadonlySet<FlipAnim> = new Set(["heelflip", "treflip", "hardflip", "varialheel"]);
/** The arms out wide, before the hands reach for the grips. */
const ARMS_OUT = { out: 1.5, back: 0.1, bend: 0.2 } as const;
/** Where the rider stands along the deck (z, the scooter's space), and each foot on it. */
const STAND_Z = 0.02;
const FRONT_FOOT_Z = 0.12;
const BACK_FOOT_Z = -0.12;
/** The hips over the deck stood on it, knees a little bent, and at most this high with a foot down kicking (m). */
const STAND_HIP = 0.66;
const KICK_HIP = 0.56;
/** How far back the kicking foot sweeps (m). */
const KICK_SWEEP = 0.3;
/** How high the feet come up off the deck at the height of a whip (m). */
const FEET_LIFT = 0.12;
/** The kick's pace (radians a second): about a kick a second. */
const KICK_RATE = 6.5;
/** How fast (a share a second) the legs straighten out behind for a Superman, and bend back after. */
const STRETCH_RATE = 3;
/** Walking it: how far out from the rider's middle the hand holds the near grip, and how far back the scooter sits (m). */
const WALK_HAND_OUT = 0.25;
const WALK_Z = -0.15;
/** The bri flip turns about the line through the grips; the finger whip about the stem. */
const GRIP_LINE = new THREE.Vector3(0, SCOOTER_GRIP.y, SCOOTER_HEAD_Z + SCOOTER_GRIP.z);
const STEM_AXIS = new THREE.Vector3(0, 0, SCOOTER_HEAD_Z);
const SC_PIVOT = new THREE.Vector3();
const SC_BASE = new THREE.Vector3();
const SC_Q = new THREE.Quaternion();
const TMP_Q3 = new THREE.Quaternion();
const TMP_Q4 = new THREE.Quaternion();
const TMP_Q5 = new THREE.Quaternion();
const X_AXIS = new THREE.Vector3(1, 0, 0);
const Y_AXIS = new THREE.Vector3(0, 1, 0);
const Z_AXIS = new THREE.Vector3(0, 0, 1);
const TMP_V = new THREE.Vector3();
const TMP_V2 = new THREE.Vector3();
const TMP_V3 = new THREE.Vector3();
const TMP_V4 = new THREE.Vector3();
